export const STAFF_ROLES = [
  "admin",
  "director",
  "arranger",
  "office_staff",
  "read_only",
  "support",
  "staff",
] as const;

export type StaffRole = (typeof STAFF_ROLES)[number];

export type StaffAccess =
  | {
      authorized: true;
      role: StaffRole;
      funeralHomeId: string;
      locationId: string | null;
      locationRestricted: boolean;
      locationContext: {
        locationId: string | null;
        locationIds: string[];
        restricted: boolean;
      };
    }
  | {
      authorized: false;
      reason:
        | "inactive"
        | "invalid-role"
        | "missing-funeral-home"
        | "missing-location";
    };

export const STAFF_MAGIC_LINK_COOLDOWN_MS = 60_000;
export const CENTRAL_STAFF_MFA_ENABLED = false;

export const CENTRAL_STAFF_INTEGRATION_BOUNDARIES = {
  centralScope:
    "Central owns staff authentication, role, capability, tenant, and location access.",
  correctionOwner: "Arrangement Studio",
  familyLinkOwner: "Arrangement Studio",
} as const;

export function getStaffAccess(claims: Record<string, unknown>): StaffAccess {
  if (claims.active !== true) {
    return { authorized: false, reason: "inactive" };
  }

  if (!isStaffRole(claims.role)) {
    return { authorized: false, reason: "invalid-role" };
  }

  if (
    typeof claims.funeralHomeId !== "string" ||
    !claims.funeralHomeId.trim()
  ) {
    return { authorized: false, reason: "missing-funeral-home" };
  }

  const locationRestricted = claims.locationRestricted === true;
  const locationId = normalizeLocationClaim(claims.locationId);
  if (locationRestricted && !locationId) {
    return { authorized: false, reason: "missing-location" };
  }

  return {
    authorized: true,
    role: claims.role,
    funeralHomeId: claims.funeralHomeId.trim(),
    locationId: locationRestricted ? locationId : null,
    locationRestricted,
    locationContext: {
      locationId: locationRestricted ? locationId : null,
      locationIds: locationRestricted && locationId ? [locationId] : [],
      restricted: locationRestricted,
    },
  };
}

export function isStaffRole(value: unknown): value is StaffRole {
  return typeof value === "string" && STAFF_ROLES.includes(value as StaffRole);
}

export function requiresAdminMfaEnrollment(
  role: StaffRole,
  enrolledFactorCount: number,
) {
  return (
    CENTRAL_STAFF_MFA_ENABLED && role === "admin" && enrolledFactorCount < 1
  );
}

export function normalizeStaffEmail(email: string) {
  return email.trim().toLowerCase();
}

export function getStaffMagicLinkCooldown(
  lastRequestedAt: number | null,
  now = Date.now(),
) {
  if (lastRequestedAt === null || !Number.isFinite(lastRequestedAt)) return 0;
  const elapsed = Math.max(0, now - lastRequestedAt);
  return Math.max(0, STAFF_MAGIC_LINK_COOLDOWN_MS - elapsed);
}

export function getStaffAuthMessage(code: string, context: "password" | "email" = "password") {
  if (code === "auth/too-many-requests") {
    return "Too many attempts were made. Wait a few minutes, then try again or reset your password.";
  }

  if (["auth/network-request-failed", "functions/internal", "functions/unavailable", "functions/deadline-exceeded"].includes(code)) {
    return "Central couldn’t reach the sign-in service. Check your connection and try again.";
  }

  if (code === "auth/invalid-email") {
    return "Enter a valid staff email address and try again.";
  }

  if (code === "auth/user-disabled") {
    return "This staff account is disabled. Contact your funeral home administrator.";
  }

  if (code === "auth/multi-factor-auth-required") {
    return "Enter your second verification factor to finish signing in.";
  }

  if (code === "auth/invalid-verification-code") {
    return "That verification code is not valid. Enter the current six-digit code from your authenticator app.";
  }

  if (code === "auth/code-expired") {
    return "That verification code has expired. Enter the newest code from your authenticator app.";
  }

  if (code === "auth/expired-action-code") {
    return "That secure sign-in link has expired. Request a new link and try again.";
  }

  if (code === "auth/invalid-action-code") {
    return "That secure sign-in link is invalid, was already used, or was replaced. Request a new link.";
  }

  return context === "email"
    ? "Central couldn’t complete your email-link request. Please try again."
    : "We couldn’t verify that email and password. Check them and try again.";
}

function normalizeLocationClaim(value: unknown) {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return /^[A-Za-z0-9][A-Za-z0-9_-]{1,79}$/.test(normalized)
    ? normalized
    : null;
}
